import React, { useState, useEffect } from "react";
import { Box, Grid, TextField, Button, Typography, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { updateDebtData, setDateError } from "../../../redux/slices/updateDataSlice";
import { getDebt } from "redux/slices/debtSlice";
import { Category, CommentKey, CommentSectionName, Key } from "../../../constant";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import { FroalaRichText } from "components/util/FroalaRichtext";
import { DapComment, updateDapCommentData } from "redux/slices/updateDapCommentSlice";
import { saveDapCommentData } from "redux/slices/saveCommentSlice";

const FormStyle = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: `${theme.spacing(8)} !important`,
  textAlign: "left",
  color: theme.palette.text.secondary,
  marginTop: `${theme.spacing(5)} !important`,
}));



export const DebtForm: React.FC<any> = (props) => {
  const dispatch = useAppDispatch();
  const debtData = useAppSelector((state) => state.debt);
  const updatedDebtData = useAppSelector((state) => state.updateData.debtData);
  const assetPlanId = useAppSelector(
    (state) => state.getAssetPlanId.assetPlanId
  );
  const dateError = useAppSelector((state) => state.updateData.dateError || "");
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "";
    return dateString.split('T')[0];
  };

  useEffect(() => {
    if (assetPlanId) {
      dispatch(getDebt(assetPlanId))
    }
  }, [assetPlanId])

  useEffect(() => {
    if (debtData?.data) {
      dispatch(updateDebtData(debtData.data));
    }
  }, [debtData?.data]);


  const handelValueChange = (event: any) => {
    dispatch(
      updateDebtData({
        ...updatedDebtData,
        lender: event.target.value,
      }))
  }

          const comments = useAppSelector((state) => state.dapComments?.data).find(
              (x) => x.commentKey.toLowerCase() === CommentKey.DEBT.toLowerCase()
            );
    
              const handleCommentsChange = (newValue: string) => {
                const updatedcomment: DapComment = {
                  id: comments ? comments.id : 0,
                  commentKey: comments ? comments.commentKey : CommentKey.DEBT,
                  commentText: newValue || "",
                  sectionName: CommentSectionName.DEBT,
                  assetPlanId: assetPlanId,
                };
                dispatch(updateDapCommentData(updatedcomment));
                dispatch(saveDapCommentData(updatedcomment));
              };


  return (
    <div>

      <FormStyle>
        {debtData.loading ? (
          <Skeleton
            variant="rounded"
            width="100%"
            height={280}
            animation="wave"
          />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Grid> <b> Lender </b></Grid>
              <Grid>
                <TextField size='small'
                  fullWidth={true}
                  disabled={props.mode === "view"}
                  InputProps={{
                    style: {
                      margin: "0px",
                      fontFamily:
                        "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  value={updatedDebtData?.lender}
                  onChange={(event) => { handelValueChange(event) }}></TextField>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid><b> Interest Rate</b></Grid>
              <Grid>
                <TextField size='small'
                  fullWidth={true}
                  disabled={props.mode === "view"}
                  InputProps={{
                    style: {
                      margin: "0px",
                      fontFamily:
                        "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  value={updatedDebtData?.interestRate}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    dispatch(
                      updateDebtData({
                        ...updatedDebtData,
                        interestRate: newValue,
                      })
                    );
                  }}></TextField>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid><b> Maturity</b></Grid>
              <Grid>
                <TextField size='small'
                  fullWidth={true}
                  type="date"
                  disabled={props.mode === "view"}
                  error={!!dateError}
                  helperText={dateError}
                  InputProps={{
                    style: {
                      margin: "0px",
                      fontFamily:
                        "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  value={formatDate(updatedDebtData?.maturity)}
                  onChange={(event) => {
                    const newValue = event.target.value;                   
                    if (newValue) {
                      const date = new Date(newValue);
                      if (isNaN(date.getTime()) || newValue !== date.toISOString().split('T')[0]) {
                        dispatch(setDateError("Please enter a valid date"));
                      } else {
                        dispatch(setDateError(""));
                      }
                    } else {
                      dispatch(setDateError(""));
                    }
                    
                    dispatch(
                      updateDebtData({
                        ...updatedDebtData,
                        maturity: newValue,
                      })
                    );
                  }}></TextField>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid><b> Outstanding Balance</b></Grid>
              <Grid>
                <TextField size='small'
                  type="number"
                  onKeyDown={(e) => {
                    if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                      e.preventDefault();
                    }
                  }}
                  disabled={props.mode === "view"}
                  fullWidth={true} InputProps={{
                    style: {
                      margin: "0px",
                      fontFamily:
                        "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  value={updatedDebtData?.outstandingBalance}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    dispatch(
                      updateDebtData({
                        ...updatedDebtData,
                        outstandingBalance: Number(newValue),
                      })
                    );
                  }}></TextField>
              </Grid>
            </Grid>


          </Grid>
        )}
      </FormStyle>
      
             <form className="m-20 lease-form">
                                 <fieldset>
                                   <legend className="lease-title">Comments</legend>
                                   <>
                                     {props?.mode === "edit" && (
                                       <FroalaRichText
                                         model={comments?.commentText || ""}
                                         handlesModelChange={handleCommentsChange}
                                       />
                                     )}
                         
                                     {(props?.mode === undefined || props?.mode === "view") && (
                                       <FroalaEditorView model={comments?.commentText || ""} />
                                     )}
                                   </>
                                 </fieldset>
                               </form>
    </div>

  );
};